import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Save, Settings, QrCode, Tag, MapPin, Smartphone, Hash, Type } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const LabelConfig = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    width_mm: 50,
    height_mm: 25,
    show_qr_code: true,
    show_imei: true,
    show_grade: true,
    show_bin_location: true,
    show_brand_model: true,
    show_did: true,
    font_size_did: 14,
    font_size_details: 10,
    qr_size_mm: 20
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/warehouse/label-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setConfig(response.data.config);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/warehouse/label-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        alert('Label configuration saved successfully!');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleField = (field) => {
    setConfig(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const updateValue = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Field configuration items
  const fieldOptions = [
    { key: 'show_qr_code', label: 'QR Code', icon: QrCode, description: 'Scannable QR with DID' },
    { key: 'show_did', label: 'Device ID (DID)', icon: Hash, description: '8-digit unique identifier' },
    { key: 'show_imei', label: 'IMEI Number', icon: Smartphone, description: 'Device IMEI' },
    { key: 'show_grade', label: 'Grade', icon: Tag, description: 'Quality grade (Z1-Z7)' },
    { key: 'show_bin_location', label: 'Bin Location', icon: MapPin, description: 'Storage bin ID' },
    { key: 'show_brand_model', label: 'Brand & Model', icon: Type, description: 'Device make and model' },
  ];

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2b3380] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/warehouse/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ color: ZOBOX_BLUE }}>
                DID Label Configuration
              </h1>
              <p className="text-sm text-gray-500">Configure label size and visible fields</p>
            </div>
          </div>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
            style={{ backgroundColor: ZOBOX_BLUE }}
            data-testid="save-config-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Label Size */}
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="label-size-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${ZOBOX_BLUE}15` }}>
                <Settings className="w-5 h-5" style={{ color: ZOBOX_BLUE }} />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">Label Size</h2>
                <p className="text-sm text-gray-500">Fixed size: 50mm × 25mm</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Width (mm)</label>
                <input
                  type="number"
                  value={config.width_mm}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Height (mm)</label>
                <input
                  type="number"
                  value={config.height_mm}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                />
              </div>
            </div>

            {/* Font Size Settings */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Font Sizes</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">DID Font Size</label>
                  <input
                    type="number"
                    value={config.font_size_did}
                    onChange={(e) => updateValue('font_size_did', parseInt(e.target.value) || 14)}
                    min="8"
                    max="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                    data-testid="font-size-did"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Details Font Size</label>
                  <input
                    type="number"
                    value={config.font_size_details}
                    onChange={(e) => updateValue('font_size_details', parseInt(e.target.value) || 10)}
                    min="6"
                    max="14"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                    data-testid="font-size-details"
                  />
                </div>
              </div>
            </div>

            {/* QR Size */}
            <div className="mt-4">
              <label className="block text-xs text-gray-500 mb-1">QR Code Size (mm)</label>
              <input
                type="number"
                value={config.qr_size_mm}
                onChange={(e) => updateValue('qr_size_mm', parseInt(e.target.value) || 20)}
                min="10"
                max="22"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]/20 focus:border-[#2b3380]"
                data-testid="qr-size"
              />
            </div>
          </div>

          {/* Label Preview */}
          <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="label-preview-section">
            <h2 className="font-semibold text-gray-900 mb-4">Label Preview</h2>
            
            {/* Actual size preview */}
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-3 bg-white mx-auto"
              style={{ 
                width: `${config.width_mm * 3}px`, 
                height: `${config.height_mm * 3}px`,
                display: 'flex',
                alignItems: 'stretch'
              }}
            >
              {/* QR Code area */}
              {config.show_qr_code && (
                <div 
                  className="flex-shrink-0 border border-gray-200 rounded flex items-center justify-center bg-gray-50"
                  style={{ width: `${config.qr_size_mm * 3}px`, height: '100%' }}
                >
                  <QrCode className="w-8 h-8 text-gray-400" />
                </div>
              )}
              
              {/* Text content area */}
              <div className="flex-1 pl-2 flex flex-col justify-center overflow-hidden">
                {config.show_did && (
                  <div 
                    className="font-bold text-gray-900 truncate"
                    style={{ fontSize: `${config.font_size_did}px` }}
                  >
                    DID-00000001
                  </div>
                )}
                {config.show_brand_model && (
                  <div 
                    className="text-gray-600 truncate"
                    style={{ fontSize: `${config.font_size_details}px` }}
                  >
                    Samsung Galaxy S24
                  </div>
                )}
                {config.show_grade && (
                  <div 
                    className="text-gray-500 truncate"
                    style={{ fontSize: `${config.font_size_details}px` }}
                  >
                    Grade: Z1
                  </div>
                )}
                {config.show_imei && (
                  <div 
                    className="text-gray-400 truncate"
                    style={{ fontSize: `${config.font_size_details - 2}px` }}
                  >
                    IMEI: 123456789012345
                  </div>
                )}
                {config.show_bin_location && (
                  <div 
                    className="text-gray-400 truncate"
                    style={{ fontSize: `${config.font_size_details - 2}px` }}
                  >
                    Bin: WH-L1-R2-S1-B3
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-xs text-gray-400 text-center mt-2">
              Preview scaled 3× (actual: {config.width_mm}mm × {config.height_mm}mm)
            </p>
          </div>
        </div>

        {/* Field Toggles */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6" data-testid="field-toggles-section">
          <h2 className="font-semibold text-gray-900 mb-4">Visible Fields</h2>
          <p className="text-sm text-gray-500 mb-4">Select which fields to display on the label</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fieldOptions.map((field) => {
              const Icon = field.icon;
              const isEnabled = config[field.key];
              
              return (
                <div
                  key={field.key}
                  onClick={() => toggleField(field.key)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    isEnabled 
                      ? 'border-[#2b3380] bg-[#2b3380]/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`toggle-${field.key}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isEnabled ? 'bg-[#2b3380]/10' : 'bg-gray-100'}`}>
                      <Icon className={`w-4 h-4 ${isEnabled ? 'text-[#2b3380]' : 'text-gray-400'}`} />
                    </div>
                    <div className="flex-1">
                      <div className={`font-medium ${isEnabled ? 'text-[#2b3380]' : 'text-gray-700'}`}>
                        {field.label}
                      </div>
                      <div className="text-xs text-gray-500">{field.description}</div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isEnabled ? 'border-[#2b3380] bg-[#2b3380]' : 'border-gray-300'
                    }`}>
                      {isEnabled && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Settings className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-blue-900">Label Printing Info</h3>
              <p className="text-sm text-blue-700 mt-1">
                Labels are designed for thermal printers with 50mm × 25mm label rolls. 
                The DID is an 8-digit unique identifier generated during GRN processing.
                QR code contains the DID for easy scanning.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </Layout>
  );
};

export default LabelConfig;
